import { Immutable } from "immer";
import React, {
    type JSX,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { Link } from "tsi-common-react/src/components/Link/index";
import { CSR_TOOLBAR_HEIGHT } from "tsi-common-react/src/styles/csr-toolbar";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { assertNever } from "tsi-common-react/src/utils/never";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import iconHamburger from "../../assets/svg/hamburger.svg";
import iconX from "../../assets/svg/x.svg";
import {
    IMenuDjangoLink,
    IMenuExternalLink,
    IMenuItem,
    IMenuPageLink,
    IMenuSnippetLink,
} from "./menu.interface";

import styles from "./index.module.scss";

const checkIfInReviewsSection = (reviewsEle: HTMLElement | null): boolean => {
    const document = getDocument();
    if (!reviewsEle || !document) {
        return false;
    }
    const scrollTop = document.body.scrollTop;
    const reviewsY = reviewsEle.getBoundingClientRect().top;
    return reviewsY < scrollTop + 300;
};

const getMenuButtonClass = (
    slug: string,
    url: string,
    reviewsEle: HTMLElement | null,
    reviewsSection: boolean,
    isMobile: boolean,
): string => {
    if (isMobile) {
        return "";
    }

    const menuButtonActiveClass = concatClassNames([
        styles.siteNavMenuButton,
        styles.siteNavMenuButtonActive,
        `site-nav__main-menu-item--${slug}`,
    ]);

    const menuButtonInactiveClass = concatClassNames([
        styles.siteNavMenuButton,
        `site-nav__main-menu-item--${slug}`,
    ]);

    let menuButtonClass = "";
    const window = getWindow();

    if (window?.location.href.includes(url)) {
        if (window.location.hash === "#reviews") {
            if (slug !== "shop") {
                menuButtonClass = menuButtonActiveClass;
            } else {
                menuButtonClass = menuButtonInactiveClass;
            }
        } else {
            menuButtonClass = menuButtonActiveClass;
        }
    } else {
        if (slug === "account") {
            if (window?.location.href.includes(slug)) {
                menuButtonClass = menuButtonActiveClass;
            } else {
                menuButtonClass = menuButtonInactiveClass;
            }
        } else {
            menuButtonClass = menuButtonInactiveClass;
        }
    }

    if (reviewsEle) {
        if (slug === "shop") {
            menuButtonClass = reviewsSection
                ? menuButtonInactiveClass
                : menuButtonActiveClass;
        } else if (slug === "reviews") {
            menuButtonClass = reviewsSection
                ? menuButtonActiveClass
                : menuButtonInactiveClass;
        }
    }

    return menuButtonClass;
};

const renderLink = (
    slug: string,
    url: string,
    overrideTitle: string,
    isMobile: boolean,
    linkID: number,
    reviewsEle: HTMLElement | null,
    reviewsSection: boolean,
    isSSR: boolean,
): JSX.Element => {
    const linkClass = concatClassNames([
        styles.mainDropdownMenuMobile,
        styles.mainDropdownMenuMobileLink,
        `main-dropdown-menu__mobile--${slug}`,
    ]);

    const menuItemClass = concatClassNames([
        styles.siteNavMainMenuItem,
        `site-nav__main-menu-item--${slug}`,
    ]);

    // Only compute dynamic classNames on the client to ensure SSR
    // and client markup match and prevent hydration mismatch.
    const menuButtonClass = !isSSR
        ? getMenuButtonClass(slug, url, reviewsEle, reviewsSection, isMobile)
        : undefined;

    if (isMobile) {
        return (
            <a key={linkID} href={url} className={linkClass}>
                {overrideTitle}
            </a>
        );
    }

    return (
        <span className={menuButtonClass} key={linkID}>
            <a className={menuItemClass} href={url}>
                {overrideTitle}
            </a>
        </span>
    );
};

const buildMenuLinks = (
    menuItems: readonly IMenuItem[],
    isMobile: boolean,
    isSubNav: boolean,
    reviewsEle: HTMLElement | null,
    reviewsSection: boolean,
    isSSR: boolean,
): JSX.Element[] => {
    let index = 0;

    const buildPageLinks = (menuItem: IMenuPageLink, isSSR: boolean) => {
        return renderLink(
            menuItem.value.page.slug,
            menuItem.value.page.url,
            menuItem.value.override_title,
            isMobile,
            index++,
            reviewsEle,
            reviewsSection,
            isSSR,
        );
    };

    const buildDjangoLinks = (menuItem: IMenuDjangoLink, isSSR: boolean) => {
        return renderLink(
            menuItem.value.url_name,
            menuItem.value.url,
            menuItem.value.override_title,
            isMobile,
            index++,
            reviewsEle,
            reviewsSection,
            isSSR,
        );
    };

    const buildExternalLinks = (
        menuItem: IMenuExternalLink | IMenuSnippetLink,
        isSSR: boolean,
    ) => {
        const slug = menuItem.value.override_title
            .toLowerCase()
            .replace(/[^a-z0-9]+/, "-");
        return renderLink(
            slug,
            menuItem.value.link,
            menuItem.value.override_title,
            isMobile,
            index++,
            reviewsEle,
            reviewsSection,
            isSSR,
        );
    };

    return menuItems.reduce((memo, menuItem) => {
        switch (menuItem.type) {
            case "external_link":
            case "snippet_link":
                if (!isSubNav) {
                    memo = memo.concat(buildExternalLinks(menuItem, isSSR));
                }
                break;

            case "page_link":
                if (!isSubNav) {
                    memo = memo.concat(buildPageLinks(menuItem, isSSR));
                }
                break;

            case "django_url":
                if (!isSubNav) {
                    memo = memo.concat(buildDjangoLinks(menuItem, isSSR));
                }
                break;

            default:
                throw assertNever(menuItem);
        }
        return memo;
    }, [] as JSX.Element[]);
};

const getModalStyle = (windowWidth: number): Modal.Styles => {
    let contentPosition: React.CSSProperties["position"];
    let contentTop: React.CSSProperties["top"];
    let contentHeight: React.CSSProperties["height"];

    if (windowWidth < 500) {
        contentPosition = "fixed";
        contentTop = CSR_TOOLBAR_HEIGHT;
        contentHeight = `calc(100% - ${CSR_TOOLBAR_HEIGHT})`;
    } else {
        contentPosition = "relative";
        contentTop = `calc(160px + ${CSR_TOOLBAR_HEIGHT})`;
        contentHeight = "auto";
    }

    return {
        overlay: {
            position: "absolute",
            backgroundColor: "rgba(255, 255, 255, 0)",
        },
        content: {
            position: contentPosition,
            top: contentTop,
            left: "auto",
            right: "auto",
            width: "100%",
            padding: "20px",
            height: contentHeight,
            overflow: "hidden",
            border: "none",
            boxShadow: "0 10px 10px -5px rgba(0,0,0,.3)",
        },
    };
};

const MenuLinks = ({
    menuItems,
    isMobile,
    isSubNav,
    reviewsEle,
    reviewsSection,
    isSSR,
}: {
    menuItems: readonly IMenuItem[];
    isMobile: boolean;
    isSubNav: boolean;
    reviewsEle: HTMLElement | null;
    reviewsSection: boolean;
    isSSR: boolean;
}) => {
    const menuLinks = buildMenuLinks(
        menuItems,
        isMobile,
        isSubNav,
        reviewsEle,
        reviewsSection,
        isSSR,
    );
    return <span>{menuLinks}</span>;
};

export const MainMenu = (
    props: Immutable<{
        menuItems: IMenuItem[];
    }>,
): React.ReactNode => {
    const isSSR = useIsSSR();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [reviewsSection, setReviewsSection] = useState(false);
    const [reviewsEle, setReviewsEle] = useState<HTMLElement | null>(null);
    const [windowWidth, setWindowWidth] = useState(0);

    // Keep ref so resize handler always sees latest value without re-registering
    const modalIsOpenRef = useRef(modalIsOpen);
    modalIsOpenRef.current = modalIsOpen;

    // Sync windowWidth after hydration
    useEffect(() => {
        const win = getWindow();
        if (!win) return;
        setWindowWidth(win.innerWidth);
    }, []);

    useEffect(() => {
        const document = getDocument();
        if (!document) return;

        const element = document.querySelector<HTMLElement>("#reviews");
        setReviewsEle(element);
    }, []);

    useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const handleScroll = () => {
            const isInReviewsSection = checkIfInReviewsSection(reviewsEle);
            setReviewsSection(isInReviewsSection);
        };

        if (reviewsEle !== null) {
            handleScroll();
            window.addEventListener("scroll", handleScroll);
            return () => {
                window.removeEventListener("scroll", handleScroll);
            };
        }
    }, [reviewsEle]);

    useEffect(() => {
        const window = getWindow();
        const document = getDocument();
        if (!window || !document) return;

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            // Use ref to avoid re-registering this listener on every modal state change
            if (modalIsOpenRef.current) {
                setModalIsOpen(false);
                document.body.classList.remove("body-dropdown-open--desktop");
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
        // modalIsOpen intentionally omitted — using ref instead
    }, []);

    const handleOpenModal = useCallback(
        (event?: React.MouseEvent<HTMLElement>) => {
            const document = getDocument();
            if (!document) return;

            let menuLinkElem = null;

            if (event) {
                event.preventDefault();
                menuLinkElem = event.currentTarget;
            }

            if (menuLinkElem) {
                if (
                    !menuLinkElem.classList.contains(
                        "site-nav__main-menu-item--mobile",
                    )
                ) {
                    document.body.classList.add("body-dropdown-open--desktop");
                }
            }

            setModalIsOpen(true);
        },
        [],
    );

    const handleCloseModal = useCallback(
        (event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
            const document = getDocument();
            if (!document) return;

            let menuLinkElem: HTMLElement | null = null;

            if (event) {
                event.preventDefault();
                menuLinkElem = event.currentTarget as HTMLElement;
            }

            if (menuLinkElem) {
                if (
                    !menuLinkElem.classList.contains(
                        "site-nav__main-menu-item--mobile",
                    )
                ) {
                    document.body.classList.remove(
                        "body-dropdown-open--desktop",
                    );
                }
            }

            setModalIsOpen(false);
        },
        [],
    );

    const modalStyle = getModalStyle(windowWidth);

    return (
        <span>
            <span>
                <span>
                    <Link
                        href={null}
                        tabIndex={0}
                        title={t`Open Menu`}
                        onClick={(e) => {
                            handleOpenModal(e);
                        }}
                        aria-label={t`Open Menu`}
                        className={concatClassNames([
                            styles.siteNavMainMenuItem,
                            styles.siteNavMainMenuItemMobile,
                            "site-nav__main-menu-item--mobile",
                        ])}
                    >
                        <SVG
                            className={styles.siteNavMobileToggleIcon}
                            src={iconHamburger}
                        />
                    </Link>
                </span>
                <MenuLinks
                    menuItems={props.menuItems}
                    isMobile={false}
                    isSubNav={false}
                    reviewsEle={reviewsEle}
                    reviewsSection={reviewsSection}
                    isSSR={isSSR}
                />
            </span>
            <Modal
                aria={{ modal: true }}
                contentLabel={t`Site Navigation`}
                ariaHideApp={false}
                style={modalStyle}
                isOpen={modalIsOpen}
                onRequestClose={(e) => {
                    handleCloseModal(e);
                }}
                role="dialog"
                onAfterOpen={() => {
                    focusElement(".main-dropdown-menu__close");
                }}
            >
                <div className={styles.mainDropdownMenu}>
                    <button
                        onClick={(e) => {
                            handleCloseModal(e);
                        }}
                        className={concatClassNames([
                            styles.mainDropdownMenuMobile,
                            styles.mainDropdownMenuClose,
                            "main-dropdown-menu__close",
                        ])}
                    >
                        <SVG
                            aria-hidden="true"
                            className={styles.mainDropdownMenuCloseIcon}
                            src={iconX}
                        />
                    </button>
                    <div
                        className={concatClassNames([
                            styles.mainDropdownMenuContent,
                            "l-capped-width",
                        ])}
                        id="main-dropdown-menu-shop"
                    >
                        <MenuLinks
                            menuItems={props.menuItems}
                            isMobile={true}
                            isSubNav={false}
                            reviewsEle={reviewsEle}
                            reviewsSection={reviewsSection}
                            isSSR={isSSR}
                        />
                    </div>
                </div>
            </Modal>
        </span>
    );
};
